<template>
  <!-- 3. 告警明细（按电站聚合，站→设备两级） -->
  <section ref="sectionRef" class="report-section">
    <div class="d-flex align-center justify-space-between mb-3">
      <h2 class="section-title mb-0">
        <!-- <v-icon size="20" color="var(--color-primary)">mdi-alert-circle</v-icon> -->
        告警明细
      </h2>
      <div class="d-flex align-center ga-2">
        <div class="seg-tabs">
          <button v-for="item in priorityOptions" :key="item.value" type="button" class="seg-tab"
            :class="{ active: priorityFilter === item.value }" @click="selectPriority(item.value)">
            {{ item.label }}
          </button>
        </div>
        <v-text-field v-model="search" density="compact" variant="outlined" placeholder="请输入电站名称或id"
          prepend-inner-icon="mdi-magnify" hide-details clearable rounded="xl" class="search-input"></v-text-field>
        <v-btn v-if="hasActiveFilters" class="station-filter-clear" size="small" variant="text" color="primary"
          icon="mdi-filter-remove-outline" aria-label="取消筛选" v-tooltip:top="'取消筛选'"
          @click="$emit('clear-station-filter')"></v-btn>
      </div>
    </div>

    <div class="station-groups">
      <div v-for="group in pagedGroups" :key="group.stationId" class="station-group"
        :class="{ highlighted: highlightedStations.includes(group.stationId) }"
        :ref="el => setStationRef(group.stationId, el)">
        <!-- 一级：电站行 -->
        <div class="station-row" @click="$emit('toggle-station', group.stationId)">
          <v-icon size="20" class="expand-icon" :class="{ expanded: expandedStations.includes(group.stationId) }">
            mdi-chevron-right
          </v-icon>
          <div class="station-identity">
            <span :class="getLevelClass(group.level)" class="station-priority">{{ getLevelInfo(group.level).code
            }}</span>
            <span class="station-name-link" :title="`ID: ${group.stationId}`" @click.stop="goToStationDetail(group)">{{
              group.stationName }}</span>
          </div>
          <div class="station-metrics">
            <span class="metric unrecovered-metric">
              <span class="metric-label">未恢复</span>
              <span class="metric-value unrecovered-value">
                <span><b>{{ group.alarmCount }}</b> 条</span>
                <el-tooltip v-if="group.alarmCount" placement="bottom">
                  <template #content>
                    <div class="category-tooltip-lines">
                      <div class="tooltip-line-safety">影响安全：{{ group.categoryStats?.safety || 0 }}</div>
                      <div class="tooltip-line-income">影响收益：{{ group.categoryStats?.income || 0 }}</div>
                      <div class="tooltip-line-normal">普通告警：{{ group.categoryStats?.normal || 0 }}</div>
                    </div>
                  </template>
                  <span class="unrecovered-category-strip" aria-label="未恢复告警分类占比">
                    <span v-if="group.categoryStats?.safety" class="category-seg safety"
                      :style="{ width: getCategoryWidth(group, group.categoryStats.safety) }"></span>
                    <span v-if="group.categoryStats?.income" class="category-seg income"
                      :style="{ width: getCategoryWidth(group, group.categoryStats.income) }"></span>
                    <span v-if="group.categoryStats?.normal" class="category-seg normal"
                      :style="{ width: getCategoryWidth(group, group.categoryStats.normal) }"></span>
                  </span>
                </el-tooltip>
                <span v-else class="unrecovered-category-strip is-empty" aria-label="暂无未恢复告警分类">
                  <span class="category-seg empty"></span>
                </span>
              </span>
            </span>
            <span class="metric"><span class="metric-label">告警次数</span><span class="metric-value"><b>{{
              group.totalAlarmCount }}</b>
                次</span></span>
            <span class="metric"><span class="metric-label">涉及设备</span><span class="metric-value"><b>{{
              group.deviceCount }}</b>
                台</span></span>
            <span class="metric time"><span class="metric-label">最新触发</span><span class="metric-value">{{
              formatLatestTime(group.latestTriggerTime) }}</span></span>
          </div>
          <div class="station-actions" @click.stop>
            <v-btn size="small" variant="text" color="primary" @click="$emit('open-analysis', group)">
              整站分析<span class="vip-badge">VIP</span>
            </v-btn>
          </div>
        </div>

        <!-- 二级：设备分组（展开后） -->
        <div v-if="expandedStations.includes(group.stationId)" class="device-groups" :aria-busy="group.loading">
          <div v-if="group.loading" class="station-expand-loading">
            <v-progress-circular indeterminate color="primary" size="22" width="2" />
            <span>正在加载站内告警...</span>
          </div>
          <template v-else-if="group.devices.length">
            <div v-for="device in group.devices" :key="device.equipmentId" class="device-group">
              <div class="device-head">
                <v-icon size="15" color="#8792a6">mdi-chip</v-icon>
                <span class="device-name">{{ device.equipmentName }}</span>
                <span class="device-code">设备编码：{{ device.equipmentId || '-' }}</span>
                <span class="device-count">告警总数 {{ device.totalTriggerCount }} 次</span>
                <span class="device-count unrecovered">未恢复 {{ device.unrecoveredCount }} 条</span>
              </div>
              <div class="device-alarms">
                <div v-for="alarm in device.alarms" :key="alarm.id" class="alarm-line">
                  <span :class="getCategoryClass(alarm.category)" class="alarm-category-cell">
                    <v-icon size="14">{{ getCategoryIcon(alarm.category) }}</v-icon>
                  </span>
                  <span class="text-mono alarm-code-cell">{{ alarm.alarmCode }}</span>
                  <span class="alarm-content-cell">{{ alarm.content }}</span>
                  <span :class="getStateClass(alarm.state)" class="alarm-state-cell">{{ alarm.state }}</span>
                  <span class="alarm-trigger-cell">
                    <span class="trigger-time">{{ formatTime(alarm.lastTriggerTime) }}</span>
                    <span v-if="alarm.triggerCount > 1" class="trigger-badge">×{{ alarm.triggerCount }}</span>
                  </span>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="station-expand-empty">暂无站内告警</div>
        </div>
      </div>

      <div v-if="!loading && !groups.length" class="empty-inline">暂无符合条件的告警</div>
      <div v-if="total" class="station-pagination">
        <span>共 {{ total }} 个电站</span>
        <v-pagination :model-value="page" :length="pageCount" :total-visible="5" density="compact" size="small"
          @update:model-value="$emit('page-change', $event)" />
      </div>
      <div v-if="loading" class="station-list-loading" role="status">
        <v-progress-circular indeterminate color="primary" size="26" width="2" />
        <span>正在查询...</span>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import dayjs from 'dayjs'
import { useRouter } from 'vue-router'
import { getLevelInfo, getLevelClass } from './stationLevel'
import { getCategoryClass, getCategoryIcon } from './category'

const props = defineProps({
  groups: { type: Array, default: () => [] },
  expandedStations: { type: Array, default: () => [] },
  highlightedStations: { type: Array, default: () => [] },
  stationIdsFilter: { type: Array, default: () => [] },
  defaultPriority: { type: String, default: 'P0' },
  loading: { type: Boolean, default: false },
  page: { type: Number, default: 1 },
  pageSize: { type: Number, default: 5 },
  total: { type: Number, default: 0 },
  // 由父组件持有的 DOM 引用登记函数，用于「查看这N站」时定位滚动
  setStationRef: { type: Function, required: true },
})
const emit = defineEmits([
  'toggle-station',
  'open-analysis',
  'clear-station-filter',
  'page-change',
  'priority-change',
])

const priorityFilter = defineModel('priorityFilter', { default: 'P0' })
const search = defineModel('search', { default: '' })
const sectionRef = ref(null)
const priorityOptions = [
  { label: 'P0', value: 'P0' },
  { label: 'P1', value: 'P1' },
  { label: 'P2', value: 'P2' },
  { label: '全部', value: 'all' },
]
const hasActiveFilters = computed(() => (
  (priorityFilter.value !== 'all' && priorityFilter.value !== props.defaultPriority)
  || Boolean(String(search.value || '').trim())
  || props.stationIdsFilter.length > 0
))
const pageCount = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)))
const pagedGroups = computed(() => props.groups)

function selectPriority(value) {
  priorityFilter.value = value
  emit('priority-change', value)
}

function scrollIntoView() {
  sectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

defineExpose({ scrollIntoView })

const router = useRouter()

// 跳转电站详情（新标签页打开，不影响日报抽屉状态）
// 与 it-operations/summary/tabs/overview/index.vue 的 handleDetail 保持同样的目标页面/参数约定
function goToStationDetail(item) {
  const url = router.resolve({
    path: '/it-operations/detail',
    query: { stationId: item.stationId, tab: '告警信息' }
  })
  window.open(url.href, '_blank')
}

// 格式化时间：当天只显示时分，非当天显示月日时分
function formatTime(timeStr) {
  if (!timeStr) return '--'
  const time = dayjs(timeStr)
  const now = dayjs()
  if (time.isSame(now, 'day')) {
    return time.format('HH:mm')
  }
  return time.format('MM-DD HH:mm')
}

// 电站汇总的“最新触发”保留到秒，便于确认告警的准确发生时刻。
function formatLatestTime(timeStr) {
  if (!timeStr) return '--'
  const time = dayjs(timeStr)
  if (time.isSame(dayjs(), 'day')) return time.format('HH:mm:ss')
  return time.format('MM-DD HH:mm:ss')
}

// 状态样式（未恢复/已恢复/已自动恢复，与告警分类是两个独立维度）
function getStateClass(state) {
  if (state === '未恢复') return 'state-open'
  if (state === '已恢复' || state === '已自动恢复') return 'state-done'
  return ''
}

function getCategoryTotal(group) {
  const stats = group.categoryStats || {}
  return (stats.safety || 0) + (stats.income || 0) + (stats.normal || 0)
}

function getCategoryWidth(group, count) {
  const total = getCategoryTotal(group)
  if (!total) return '0%'
  return `${Math.max((count / total) * 100, 8)}%`
}

</script>

<style scoped lang="scss">
@import './sectionShell.scss';
@import './category.scss';
@import './stationLevel.scss';

/* 告警明细：按电站聚合 */
.seg-tabs {
  display: inline-flex;
  padding: 3px;
  gap: 4px;
  border: 1px solid rgba(58, 104, 255, 0.06);
  border-radius: 999px;
  background: #F3F6FF;
}

.seg-tab {
  min-width: 56px;
  padding: 6px 14px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: rgba(46, 46, 46, 0.6);
  font-family: 'PingFang SC', 'HarmonyOS Sans SC', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s, box-shadow 0.2s;
}

.seg-tab:hover {
  color: #2e2e2e;
  background: rgba(58, 104, 255, 0.04);
}

.seg-tab.active {
  color: #fff;
  background: #3A68FF;
  box-shadow: 0 2px 8px rgba(58, 104, 255, 0.3);
  font-weight: 600;
}

.search-input {
  width: 240px;
}

.station-filter-clear {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
}

.search-input :deep(.v-field) {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

.search-input :deep(.v-field__input) {
  min-height: 36px;
  font-size: 14px;
}

.station-groups {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 112px;
}

.station-list-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 112px;
  color: rgba(46, 46, 46, 0.6);
  font-size: 13px;
  background: rgba(255, 255, 255, 0.82);
}

.station-group {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  overflow: hidden;
  // background: #fff linear-gradient(109.37deg, #e7edff44 1.53%, #fff6 98.28%);
  transition: box-shadow 0.3s, border-color 0.3s;

  // &:hover {
  //   box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  // }

  &.highlighted {
    border-color: #3A68FF;
    box-shadow: 0 0 0 3px rgba(58, 104, 255, 0.12);
  }
}

.station-row {
  display: grid;
  grid-template-columns: 20px minmax(250px, 1fr) auto auto;
  align-items: center;
  column-gap: 12px;
  min-height: 64px;
  padding: 11px 14px;
  cursor: pointer;
  background: #fff;
  transition: background 0.2s;

  // &:hover {
  //   background: rgba(58, 104, 255, 0.04);
  // }

  .expand-icon {
    color: rgba(46, 46, 46, 0.45);
    transition: transform 0.2s;

    &.expanded {
      transform: rotate(90deg);
    }
  }
}

.station-identity {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.station-priority {
  flex-shrink: 0;
}

.station-metrics {
  display: flex;
  align-items: start;
  gap: 18px;

  .metric {
    display: grid;
    grid-template-rows: 13px 16px;
    row-gap: 4px;
    min-width: 58px;
    font-size: 13px;
    color: rgba(46, 46, 46, 0.6);

    .metric-label {
      color: rgba(46, 46, 46, 0.62);
      font-size: 11px;
      line-height: 13px;
      white-space: nowrap;
    }

    .metric-value {
      color: rgba(46, 46, 46, 0.76);
      font-size: 13px;
      line-height: 16px;
      white-space: nowrap;
    }

    b {
      color: #2e2e2e;
      font-weight: 600;
    }

    &.warn b {
      color: rgba(255, 30, 30, 1);
    }

    &.time {
      min-width: 84px;

      .metric-value {
        color: rgba(46, 46, 46, 0.56);
      }
    }

    &.unrecovered-metric {
      // min-width: 150px;
      margin-right: 16px;
    }
  }
}

.unrecovered-value {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.unrecovered-category-strip {
  display: flex;
  width: 72px;
  height: 5px;
  gap: 2px;
  overflow: hidden;
  border-radius: 3px;
  cursor: help;

  .category-seg {
    display: block;
    min-width: 6px;
    border-radius: 2px;

    &.safety {
      background: rgba(255, 30, 30, 1);
    }

    &.income {
      background: rgba(245, 158, 11, 1);
    }

    &.normal {
      background: rgba(58, 104, 255, 1);
    }

    &.empty {
      width: 100%;
      min-width: 100%;
      background: rgba(46, 46, 46, 0.08);
    }
  }
}

.category-tooltip-lines {
  display: flex;
  flex-direction: column;
  gap: 4px;
  line-height: 1.5;
}

.tooltip-line-safety {
  color: rgba(255, 30, 30, 1);
}

.tooltip-line-income {
  color: rgba(245, 158, 11, 1);
}

.tooltip-line-normal {
  color: rgba(58, 104, 255, 1);
}


.station-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  :deep(.v-btn) {
    border-radius: 999px;
    font-size: 13px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
  }

  :deep(.v-btn__content) {
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }
}

.device-groups {
  padding: 0 16px 14px 44px;
  background: #ffffffb3;
}

.station-expand-loading,
.station-expand-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 76px;
  color: rgba(46, 46, 46, 0.52);
  font-size: 12px;
}

.device-group {
  &+.device-group {
    margin-top: 8px;
  }
}

.device-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0 6px;

  .device-name {
    font-size: 13px;
    font-weight: 600;
    color: #2e2e2e;
  }

  .device-code {
    color: rgba(46, 46, 46, 0.5);
    font-family: inherit;
    font-size: 12px;
  }

  .device-count {
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 7px;
    font-size: 11px;
    font-weight: 500;
    color: rgba(46, 46, 46, 0.65);
    background: rgba(0, 0, 0, 0.05);
    border-radius: 999px;

    &.unrecovered {
      gap: 4px;
      color: rgba(46, 46, 46, 0.65);
      background: rgba(0, 0, 0, 0.05);

      &::before {
        content: '';
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: rgba(255, 30, 30, 1);
      }
    }
  }
}

.station-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 16px;
  color: rgba(46, 46, 46, 0.6);
  font-size: 13px;
}

.device-alarms {
  display: flex;
  flex-direction: column;
}

.alarm-line {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  border-left: 3px solid transparent;
  transition: background 0.2s;
  background: rgba(0, 0, 0, 0.015);

  &:hover {
    background: rgba(58, 104, 255, 0.04);
  }

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  .alarm-category-cell {
    flex-shrink: 0;
    // width: 96px;
    font-size: 10px;
    // font-weight: 700;
    white-space: nowrap;
  }

  .alarm-code-cell {
    flex-shrink: 0;
    width: 128px;
  }

  .alarm-content-cell {
    flex: 1;
    min-width: 0;
    color: #2e2e2e;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .alarm-state-cell {
    flex-shrink: 0;
    font-size: 11px;
  }

  .alarm-trigger-cell {
    flex-shrink: 0;
    width: 128px;
    color: rgba(46, 46, 46, 0.45);
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
  }

  .trigger-time {
    white-space: nowrap;
  }

  .trigger-badge {
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 6px;
    background: rgba(0, 0, 0, 0.05);
    color: rgba(46, 46, 46, 0.58);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }

  .alarm-line-actions {
    flex-shrink: 0;
    display: flex;
    gap: 4px;
  }
}

/* VIP 权限标识：金色角标 + 扫光（与 it-operations/summary/index.vue 的 vip-mark 保持一致） */
.vip-badge {
  position: relative;
  top: -6px;
  margin-left: 2px;
  color: #c78618;
  font-size: 9px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0;
  background: linear-gradient(105deg, #b8750f 0%, #b8750f 42%, #fff2b8 50%, #d89620 58%, #b8750f 100%);
  background-size: 280% 100%;
  background-position: 100% center;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: vip-mark-glint 4s ease-in-out infinite;
}

@keyframes vip-mark-glint {

  0%,
  62% {
    background-position: 100% center;
    filter: drop-shadow(0 0 0 rgba(214, 151, 39, 0));
  }

  78% {
    filter: drop-shadow(0 0 3px rgba(224, 161, 43, 0.55));
  }

  100% {
    background-position: 0 center;
    filter: drop-shadow(0 0 0 rgba(214, 151, 39, 0));
  }
}

@media (prefers-reduced-motion: reduce) {
  .vip-badge {
    animation: none;
    background: none;
    -webkit-text-fill-color: currentColor;
  }
}

// 电站名称：与 station-monitor/summary/index.vue 的 .station-name 保持同一套"可点击链接"视觉规范
.station-name-link {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #3A68FF;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: color 0.2s, text-decoration 0.2s ease;

  &:hover {
    color: #2854e8;
    text-decoration: underline;
  }
}

.text-mono {
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
}

/* 状态：文字 + 圆点，避免与告警等级同时形成彩色胶囊 */
.state-open {
  display: inline-flex;
  align-items: center;
  height: 20px;
  gap: 4px;
  padding: 0;
  color: rgba(255, 30, 30, 1);
  font-weight: 500;
  font-size: 11px;
  background: transparent;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }
}

.state-done {
  display: inline-flex;
  align-items: center;
  height: 20px;
  gap: 4px;
  padding: 0;
  color: #10B981;
  font-weight: 500;
  font-size: 12px;
  background: transparent;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }
}

@media (max-width: 900px) {
  .unrecovered-category-strip {
    display: none;
  }

  .station-metrics .metric.unrecovered-metric {
    min-width: 58px;
  }

  .station-metrics {
    gap: 12px;

    .metric.time {
      display: none;
    }
  }
}
</style>
